import "./App.css"
import { defaultTheme, Provider, ToastContainer } from "@adobe/react-spectrum"
import { QueryClientProvider } from "@tanstack/react-query"
import { getAuthConfig } from "./auth/config"
import {
  CARD_LIBRARY_STORAGE_KEY,
  CARD_LIBRARY_STORAGE_VERSION,
  CardLibraryStateSchema,
  EMPTY_CARD_LIBRARY,
} from "./cards/storage"
import {
  DECK_LIBRARY_STORAGE_KEY,
  DECK_LIBRARY_STORAGE_VERSION,
  DeckLibraryStateSchema,
  EMPTY_DECK_LIBRARY,
} from "./decks/storage"
import {
  EMPTY_HERO_LIBRARY,
  HERO_LIBRARY_STORAGE_KEY,
  HERO_LIBRARY_STORAGE_VERSION,
  HeroLibraryStateSchema,
} from "./heroes/storage"
import { migrateLocalStorageToIdb } from "./hooks/useIdbState"
import { AppI18nProvider } from "./i18n/I18nProvider"
import { useI18n } from "./i18n/useI18n"
import { appQueryClient } from "./query/client"
import { AppShell } from "./shell/AppShell"

// One-time migration: move existing localStorage data to IndexedDB
void migrateLocalStorageToIdb(
  CARD_LIBRARY_STORAGE_KEY,
  CardLibraryStateSchema,
  {
    defaultValue: EMPTY_CARD_LIBRARY,
    version: CARD_LIBRARY_STORAGE_VERSION,
  },
)
void migrateLocalStorageToIdb(
  DECK_LIBRARY_STORAGE_KEY,
  DeckLibraryStateSchema,
  {
    defaultValue: EMPTY_DECK_LIBRARY,
    version: DECK_LIBRARY_STORAGE_VERSION,
  },
)
void migrateLocalStorageToIdb(
  HERO_LIBRARY_STORAGE_KEY,
  HeroLibraryStateSchema,
  {
    defaultValue: EMPTY_HERO_LIBRARY,
    version: HERO_LIBRARY_STORAGE_VERSION,
  },
)

const authConfig = getAuthConfig()

const AppContent = () => {
  const { t } = useI18n()

  if (!authConfig.clerkPublishableKey) {
    return (
      <main className="app-shell">
        <section className="hero">
          <p className="eyebrow">{t("app.name")}</p>
          <h1>{t("app.authSetup.title")}</h1>
          <p className="lede">{t("app.authSetup.body")}</p>
        </section>

        <section className="panel">
          <h2>{t("app.authSetup.requiredEnv")}</h2>
          <ul className="setup-list">
            <li>
              <code>PUBLIC_CLERK_PUBLISHABLE_KEY</code>
            </li>
            <li>
              <code>PUBLIC_BOOTSTRAP_ADMIN_EMAILS</code>
            </li>
            <li>
              <code>CLERK_SECRET_KEY</code>
            </li>
          </ul>
          <p className="meta">{t("app.authSetup.bootstrapHelp")}</p>
        </section>
      </main>
    )
  }

  return (
    <Provider theme={defaultTheme}>
      <AppShell />
      <ToastContainer placement="bottom end" />
    </Provider>
  )
}

const App = () => (
  <AppI18nProvider>
    <QueryClientProvider client={appQueryClient}>
      <AppContent />
    </QueryClientProvider>
  </AppI18nProvider>
)

export default App
